import { CloudUploadOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { Alert, Button, Divider, Drawer, Form, Input, Progress, Select, Space, Table, Tag, Typography, Upload } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { useMemo, useRef, useState } from 'react'
import { api } from '../api'
import { uploadReplayFiles, type ImportProgress } from '../lib/replayImport'
import type { MapCatalogEntry, PlatformConfig, ReplaySourceFormat, SourceJob } from '../types'

const { Dragger } = Upload

const formatLabels: Record<ReplaySourceFormat, string> = {
  ycr: 'YCR SQLite',
  mcap: 'MCAP（支持多分段）',
  blackbox: 'BlackBox 旧版 SQLite',
}

const stateColors: Record<SourceJob['state'], string> = {
  queued: 'blue',
  loading: 'gold',
  active: 'green',
  failed: 'red',
}

function humanBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 B'
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB']
  const order = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  return `${(value / 1024 ** order).toFixed(order === 0 ? 0 : 2)} ${units[order]}`
}

export function DataIngest({
  open,
  config,
  maps,
  onClose,
  onImported,
}: {
  open: boolean
  config?: PlatformConfig
  maps: MapCatalogEntry[]
  onClose: () => void
  onImported: (job: SourceJob) => Promise<void> | void
}) {
  const [token, setToken] = useState('')
  const [format, setFormat] = useState<ReplaySourceFormat>('ycr')
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [name, setName] = useState('')
  const [robotId, setRobotId] = useState('')
  const [siteName, setSiteName] = useState('')
  const [mapCatalogId, setMapCatalogId] = useState<string>()
  const [jobs, setJobs] = useState<SourceJob[]>([])
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<ImportProgress>()
  const [notice, setNotice] = useState<{ type: 'success' | 'error' | 'info'; message: string }>()
  const controllerRef = useRef<AbortController | undefined>(undefined)

  const catalogOptions = useMemo(() => {
    const ids = [...new Set(maps.map((item) => item.catalogId))]
    return ids.map((id) => ({ value: id, label: `${id} · ${maps.filter((item) => item.catalogId === id).length} 张地图` }))
  }, [maps])
  const sourceFiles = fileList.map((item) => item.originFileObj).filter((file): file is NonNullable<typeof file> => Boolean(file))
  const totalBytes = sourceFiles.reduce((sum, file) => sum + file.size, 0)
  const percentage = progress?.totalBytes ? Math.min(100, Math.round(progress.completedBytes * 100 / progress.totalBytes)) : 0
  const localDirect = config?.storage.provider === 'local'

  const refreshJobs = async (signal?: AbortSignal) => {
    if (!token.trim()) throw new Error('请输入管理员令牌')
    const rows = await api.sourceJobs(token.trim(), signal)
    setJobs(rows)
    return rows
  }

  const connect = async () => {
    setNotice(undefined)
    setBusy(true)
    try {
      const rows = await refreshJobs()
      setNotice({ type: 'success', message: `管理员令牌有效，发现 ${rows.length} 个对象来源任务` })
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : String(error) })
    } finally {
      setBusy(false)
    }
  }

  const importFiles = async () => {
    setNotice(undefined)
    setBusy(true)
    const controller = new AbortController()
    controllerRef.current = controller
    try {
      const objectKeys = await uploadReplayFiles(token.trim(), format, sourceFiles, setProgress, controller.signal)
      const job = await api.activateSource(token.trim(), {
        format,
        objectKeys,
        name: name.trim() || undefined,
        robotId: robotId.trim() || undefined,
        siteName: siteName.trim() || undefined,
        mapCatalogId,
      }, controller.signal)
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)])
      let currentJob = job
      for (let attempt = 0; attempt < 1800 && currentJob.state !== 'active' && currentJob.state !== 'failed'; attempt += 1) {
        setProgress((current) => ({ completedBytes: current?.totalBytes ?? totalBytes, totalBytes: current?.totalBytes ?? totalBytes, stage: `后台${currentJob.state === 'queued' ? '排队' : '校验/索引'}中 · ${currentJob.id}` }))
        await new Promise((resolve) => window.setTimeout(resolve, 1000))
        const rows = await refreshJobs(controller.signal)
        currentJob = rows.find((item) => item.id === job.id) ?? currentJob
      }
      if (currentJob.state === 'failed') throw new Error(currentJob.error || '后台导入失败')
      if (currentJob.state !== 'active') throw new Error('后台导入仍在运行，可稍后使用管理员令牌重新查看任务')
      setNotice({ type: 'success', message: `${formatLabels[format]} 已校验、索引并热注册 ${currentJob.sessions.length} 个回放会话` })
      setFileList([])
      try {
        await onImported(currentJob)
      } catch {
        setNotice({ type: 'success', message: `${formatLabels[format]} 已成功热注册；会话列表刷新失败，请刷新页面后查看` })
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        setNotice({ type: 'info', message: '已停止本次浏览器上传或等待；已经提交的后台索引任务会继续运行' })
      } else {
        setNotice({ type: 'error', message: error instanceof Error ? error.message : String(error) })
      }
    } finally {
      controllerRef.current = undefined
      setBusy(false)
    }
  }

  const close = () => {
    if (busy) return
    setToken('')
    setJobs([])
    setFileList([])
    setNotice(undefined)
    setProgress(undefined)
    onClose()
  }

  return (
    <Drawer
      className="ingest-drawer"
      size="large"
      title={<Space><CloudUploadOutlined />回放数据上传与导入</Space>}
      open={open}
      onClose={close}
      maskClosable={!busy}
      closable={!busy}
      extra={busy ? <Button danger onClick={() => controllerRef.current?.abort()}>停止上传/等待</Button> : null}
    >
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <Alert
          showIcon
          type={config?.uploadsEnabled ? 'success' : 'warning'}
          title={config?.uploadsEnabled ? localDirect ? 'PVC 流式直传已启用 · 单文件最大 5 GiB' : `TOS 已启用 · ${config.storage.bucket}` : '当前环境未启用管理员上传接口'}
          description={localDirect
            ? '浏览器通过管理员鉴权将文件流式写入持久卷，不在 Go 内存中缓存；原始对象禁止覆盖，完成后异步校验、索引并加入回放会话列表。'
            : 'AK/SK 只存在于 Kubernetes Secret。浏览器仅获得短期预签名 URL，原始对象禁止覆盖；上传完成后由 Go 后端异步校验、索引并热注册。'}
        />
        <Form layout="vertical" disabled={busy}>
          <Form.Item label={<Space><SafetyCertificateOutlined />管理员令牌（仅保存在当前页面内存）</Space>} required>
            <Space.Compact style={{ width: '100%' }}>
              <Input.Password value={token} onChange={(event) => setToken(event.target.value)} autoComplete="off" placeholder="ADMIN_TOKEN" />
              <Button icon={<ReloadOutlined />} onClick={connect} disabled={!token.trim()}>验证并刷新任务</Button>
            </Space.Compact>
          </Form.Item>
          <div className="ingest-form-grid">
            <Form.Item label="数据格式" required>
              <Select value={format} onChange={(value) => { setFormat(value); setFileList([]) }} options={Object.entries(formatLabels).map(([value, label]) => ({ value, label }))} />
            </Form.Item>
            <Form.Item label="地图目录（可选，必须已验证站点版本）">
              <Select allowClear showSearch value={mapCatalogId} onChange={setMapCatalogId} options={catalogOptions} placeholder="不猜测同号 MapID" />
            </Form.Item>
            <Form.Item label="会话名称（可选）"><Input value={name} onChange={(event) => setName(event.target.value)} maxLength={256} /></Form.Item>
            <Form.Item label="机器人 ID（可选）"><Input value={robotId} onChange={(event) => setRobotId(event.target.value)} maxLength={256} /></Form.Item>
            <Form.Item label="站点名称（可选）" className="ingest-span-two"><Input value={siteName} onChange={(event) => setSiteName(event.target.value)} maxLength={256} /></Form.Item>
          </div>
          <Form.Item label={`原始文件 · ${formatLabels[format]}`} required>
            <Dragger
              accept={format === 'mcap' ? '.mcap' : format === 'blackbox' ? '.db,.sqlite,.sqlite3,.txt' : '.ycr,.db'}
              multiple={format !== 'ycr'}
              maxCount={format === 'ycr' ? 1 : 64}
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: next }) => setFileList(next)}
            >
              <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
              <p className="ant-upload-text">拖入文件或点击选择</p>
              <p className="ant-upload-hint">MCAP 支持最多 64 个连续分段；BlackBox 支持一个 SQLite 加配套 *Loc.txt 分段；YCR 每次一个数据库。总计 {humanBytes(totalBytes)}</p>
            </Dragger>
          </Form.Item>
          {progress && <div className="ingest-progress"><Progress percent={percentage} status={notice?.type === 'error' ? 'exception' : busy ? 'active' : 'normal'} /><Typography.Text type="secondary">{progress.stage}</Typography.Text></div>}
          {notice && <Alert showIcon type={notice.type} title={notice.message} />}
          <Button
            block
            type="primary"
            size="large"
            icon={<CloudUploadOutlined />}
            loading={busy}
            disabled={!config?.uploadsEnabled || !token.trim() || sourceFiles.length === 0}
            onClick={importFiles}
          >
            上传、校验并加入回放列表
          </Button>
        </Form>
        <Divider titlePlacement="start">对象来源任务</Divider>
        <Table<SourceJob>
          size="small"
          rowKey="id"
          dataSource={jobs}
          pagination={{ pageSize: 6, size: 'small' }}
          columns={[
            { title: '格式', dataIndex: ['source', 'format'], width: 92, render: (value) => <Tag>{String(value).toUpperCase()}</Tag> },
            { title: '来源', dataIndex: ['source', 'name'], render: (value, item) => value || item.source.objectKeys.map((key) => key.split('/').at(-1)).join(', ') },
            { title: '状态', dataIndex: 'state', width: 88, render: (value) => <Tag color={stateColors[value as SourceJob['state']]}>{String(value).toUpperCase()}</Tag> },
            { title: '会话', dataIndex: 'sessions', width: 70, render: (value: SourceJob['sessions']) => value.length },
            { title: '更新时间', dataIndex: 'updatedAt', width: 150, render: (value) => new Date(value).toLocaleString() },
          ]}
          expandable={{ expandedRowRender: (item) => <Typography.Text type={item.error ? 'danger' : 'secondary'}>{item.error || item.source.objectKeys.join('\n')}</Typography.Text> }}
          locale={{ emptyText: '输入管理员令牌并刷新后显示' }}
        />
      </Space>
    </Drawer>
  )
}
